import { useState, useEffect } from 'react';
import api from '../services/api';
import { Card, Label } from './StatsUI';

// CAD "By Quarter" card: per-person completions, rush jobs, third-party reviews
// and QC tickets filed for one calendar quarter, with arrows to step back through
// quarters (/jobs/stats/quarterly?quarter=YYYY-Qn).

const COLS = [
  { key: 'completed',  label: 'Completed',    color: 'text-white' },
  { key: 'rush',       label: 'Rush',         color: 'text-red-400' },
  { key: 'reviews',    label: '3rd Party Reviews', color: 'text-purple-400' },
  { key: 'qc_created', label: 'QC Tickets Created', color: 'text-yellow-400' },
];

export default function QuarterlyByPersonCard() {
  const [offset, setOffset] = useState(0); // 0 = current quarter, -1 = previous, …
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const now = new Date();
  const idx = now.getUTCFullYear() * 4 + Math.floor(now.getUTCMonth() / 3) + offset;
  const quarter = `${Math.floor(idx / 4)}-Q${(idx % 4) + 1}`;
  const isCurrent = offset === 0;

  useEffect(() => {
    let stale = false;
    setLoading(true);
    setError('');
    api.get(`/jobs/stats/quarterly?quarter=${quarter}`)
      .then((res) => { if (!stale) setData(res.data); })
      .catch(() => { if (!stale) setError("Couldn't load that quarter."); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [quarter]);

  const rows = data?.rows || [];
  const totals = Object.fromEntries(COLS.map((c) => [c.key, rows.reduce((s, r) => s + Number(r[c.key]), 0)]));

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <Label>By Person — Quarterly</Label>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setOffset(offset - 1)}
            className="text-gray-400 hover:text-white px-1.5 leading-none text-sm"
            title="Previous quarter"
          >‹</button>
          <span className="text-[11px] font-bold text-gray-300 min-w-[6.5rem] text-center">
            {quarter.replace('-', ' ')}
            {isCurrent && <span className="text-gray-600 font-normal"> · now</span>}
          </span>
          <button
            onClick={() => setOffset(Math.min(0, offset + 1))}
            disabled={isCurrent}
            className="text-gray-400 hover:text-white px-1.5 leading-none text-sm disabled:opacity-30"
            title="Next quarter"
          >›</button>
        </div>
      </div>

      {loading && <p className="text-gray-500 text-sm mt-3">Loading…</p>}
      {error && <p className="text-red-400 text-sm mt-3">{error}</p>}

      {!loading && !error && (
        rows.length === 0 ? (
          <p className="text-gray-500 text-sm mt-3">No activity {isCurrent ? 'this quarter yet' : 'that quarter'}.</p>
        ) : (
          <table className="w-full text-xs mt-3">
            <thead>
              <tr className="text-gray-500 uppercase tracking-wide text-[10px]">
                <th className="text-left font-semibold py-1.5">Person</th>
                {COLS.map((c) => <th key={c.key} className="text-right font-semibold py-1.5 pl-3">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="border-t" style={{ borderColor: '#1e3a5f' }}>
                  <td className="text-gray-300 py-1.5">{r.name}</td>
                  {COLS.map((c) => (
                    <td key={c.key} className={`text-right py-1.5 pl-3 ${Number(r[c.key]) ? c.color : 'text-gray-600'}`}>
                      {r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t font-bold" style={{ borderColor: '#c9a84c' }}>
                <td className="text-gray-400 py-1.5">Total</td>
                {COLS.map((c) => <td key={c.key} className="text-right text-gray-200 py-1.5 pl-3">{totals[c.key]}</td>)}
              </tr>
            </tbody>
          </table>
        )
      )}
    </Card>
  );
}
